import classNames from "classnames";
import React from "react";
import { LinkInfo } from "../../models/shared/LinkInfo";
import { isNullOrWhiteSpace } from "../../shared/string";
import { isValidLink } from "../../utilities/links";
import Button, { ButtonSize } from "../Buttons/Button";
import SanityLink from "../SanityLink";
import { PortableTextColorTheme } from "./PortableText";

export type PortableTextTableProps = {
  title?: string;
  rows?: {
    cells: {
      value?: string;
    }[];
  }[];
  primaryCtaLink?: LinkInfo;
  secondaryCtaLink?: LinkInfo;
  noteBlock?: {
    showNoteBlock?: boolean;
    title?: string;
    preamble?: string;
    ctaLink?: LinkInfo;
    links?: LinkInfo[];
  };
  darkTheme?: boolean;
  portableTextColorTheme?: PortableTextColorTheme;
};

const PortableTextTable = ({
  rows: allRows,
  title,
  primaryCtaLink,
  secondaryCtaLink,
  noteBlock,
  darkTheme,
  portableTextColorTheme
}: PortableTextTableProps) => {
  const [header, ...rows] = allRows || [];
  let headerColor = "brand-primary-dim";

  if (portableTextColorTheme) {
    switch (portableTextColorTheme) {
      case PortableTextColorTheme.PrimaryDark:
        headerColor = "brand-primary-80";
        break;
    }
  }

  return (
    <div
      className={
        darkTheme
          ? "bg-brand-primary-20 py-[3.75rem] md:py-[5rem] xl:py-[6.25rem] mb-12 lg:mb-32"
          : ""
      }
    >
      <aside className={darkTheme ? "mb-0" : ""}>
        {!!title?.length && (
          <h2 className={darkTheme ? "text-white" : "text-current"}>{title}</h2>
        )}
        <div className="overflow-auto mt-[3.75rem] md:mt-[6.25rem]">
          <table
            className={classNames(
              "w-max md:w-full min-w-table",
              darkTheme ? "text-white" : ""
            )}
          >
            <thead>
              <tr
                className={classNames(
                  `text-${headerColor} border-b  text-base font-bold`,
                  darkTheme
                    ? "text-white border-brand-primary-dim"
                    : "border-brand-primary-95"
                )}
              >
                {header?.cells.map((cell, index) => (
                  <th key={index} className="uppercase text-left pr-8 py-3">
                    {cell.value}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows?.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={classNames(
                    "text-lg no-underline",
                    row.cells?.some(x => x.isNote)
                      ? ""
                      : `border-b ${
                          darkTheme
                            ? "border-brand-primary-dim"
                            : "border-brand-primary-95"
                        }`
                  )}
                >
                  {row.cells?.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="font-normal text-left pr-8 max-w-td-mobile md:max-w-td-desktop md:min-w-32 py-3"
                    >
                      {cell.value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(isValidLink(primaryCtaLink) || isValidLink(secondaryCtaLink)) && (
          <div className="flex flex-col md:flex-row justify-center mt-16 gap-8">
            {isValidLink(primaryCtaLink) && (
              <Button
                size={ButtonSize.Normal}
                color="primary-light"
                type="link"
                {...primaryCtaLink}
              >
                {primaryCtaLink.title}
              </Button>
            )}
            {isValidLink(secondaryCtaLink) && (
              <Button
                size={ButtonSize.Normal}
                color={darkTheme ? "primary-transparent" : "primary-inverted"}
                type="link"
                {...secondaryCtaLink}
              >
                {secondaryCtaLink.title}
              </Button>
            )}
          </div>
        )}

        {noteBlock?.showNoteBlock && (
          <div
            className={classNames(
              "break-words border-solid border border-brand-primary-95 mt-[6.25rem] p-[1.875rem] md:p-12  rounded-[1.25rem]",
              darkTheme ? "bg-brand-primary-100" : "bg-brand-primary-20"
            )}
          >
            {!isNullOrWhiteSpace(noteBlock?.title) && (
              <h3
                className={classNames(
                  "text-2xl md:text-3xl",
                  darkTheme ? "" : "text-white"
                )}
              >
                {noteBlock.title}
              </h3>
            )}
            {!isNullOrWhiteSpace(noteBlock?.preamble) && (
              <p
                className={classNames(
                  "text-base md:text-xl mt-5 mb-0",
                  darkTheme ? "" : "text-white"
                )}
              >
                {noteBlock.preamble}
              </p>
            )}

            {noteBlock?.links && (
              <div className="mt-[1.875rem] flex flex-col gap-5 md:flex-row md:flex-wrap md:gap-x-[1.875rem] md:gap-y-2">
                {noteBlock?.links?.map(link => (
                  <SanityLink {...link} className={darkTheme ? "" : "text-white"}>
                    {link.title}
                  </SanityLink>
                ))}
              </div>
            )}

            {isValidLink(noteBlock?.ctaLink) && (
              <div className="flex mt-[1.875rem]">
                <Button
                  className="xl:mr-6 mt-[1.87rem] w-full md:w-auto"
                  size={ButtonSize.Normal}
                  color={darkTheme ? "primary-light" : "primary-inverted"}
                  type="link"
                  {...noteBlock?.ctaLink}
                >
                  {noteBlock?.ctaLink?.title}
                </Button>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export default PortableTextTable;
